import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '../ui/dialog';
import MfaCodeInput from './MfaCodeInput';
import { API } from '../../lib/api';
import { SESSION_EXPIRED_EVENT, storeSessionTokens, whenRefreshSettled } from '../../lib/authRefresh';
import { parseMfaError, RECOVERY_CODE_USED_MESSAGE } from '../../lib/mfaErrors';
import {
    allowStepUpPrompt,
    cancelStepUp,
    markStepUpUnavailable,
    registerStepUpHost,
    resolveStepUp,
} from '../../lib/stepUp';

/**
 * "Confirm it's you" — the in-session MFA step-up prompt (issue #854).
 *
 * Opened by `lib/stepUp.js` when a request comes back with the MFA 403. A good
 * code (or recovery code) goes to `POST /api/auth/mfa/step-up`, which returns a
 * new token pair carrying the code factor; the pair is stored exactly the way
 * a silent refresh stores it, and the waiting requests are sent again.
 *
 * The response deliberately carries no `user`: replacing the `/auth/me` user
 * with a login-shaped one would drop fields such as the org logo and app links.
 */
export default function StepUpDialog({ user }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const [code, setCode] = useState('');
    const [recoveryMode, setRecoveryMode] = useState(false);
    const [error, setError] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [notEnrolled, setNotEnrolled] = useState(false);

    useEffect(() => registerStepUpHost(() => {
        setCode('');
        setRecoveryMode(false);
        setError(null);
        setVerifying(false);
        setNotEnrolled(false);
        setOpen(true);
    }), []);

    // A cancel keeps the prompt from re-opening on this page; a new page may ask again.
    useEffect(() => {
        allowStepUpPrompt();
    }, [pathname]);

    // The session ended (refresh refused) while the prompt was up: it can't be answered.
    useEffect(() => {
        const onExpired = () => {
            setOpen(false);
            cancelStepUp();
        };
        window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    }, []);

    const cancel = () => {
        setOpen(false);
        cancelStepUp();
    };

    const verify = async (submitted) => {
        const theCode = (submitted ?? code).trim();
        if (!theCode || verifying) return;
        setVerifying(true);
        setError(null);
        try {
            // The step-up retires the refresh token it is sent, so a refresh
            // already using that token must finish first.
            await whenRefreshSettled();
            const token = localStorage.getItem('crm_token');
            const res = await axios.post(
                `${API}/auth/mfa/step-up`,
                { code: theCode, refresh_token: localStorage.getItem('crm_refresh_token') },
                { headers: token ? { Authorization: `Bearer ${token}` } : {} },
            );
            const { access_token: accessToken, refresh_token: refreshToken, warning } = res.data;
            storeSessionTokens(accessToken, refreshToken);
            setOpen(false);
            resolveStepUp(accessToken);
            if (warning) toast.warning(RECOVERY_CODE_USED_MESSAGE);
        } catch (err) {
            const parsed = parseMfaError(err);
            if (parsed.code === 'mfa_not_enrolled') {
                setNotEnrolled(true);
            } else if (parsed.code === 'step_up_unavailable') {
                // This session can never step up ("view as", SSO): stop asking.
                setOpen(false);
                markStepUpUnavailable();
                toast.error(parsed.message);
            } else if (parsed.code === 'token_expired') {
                cancel();
                toast.error('Your session expired. Sign in again.');
            } else {
                setError(parsed.message);
                setCode('');
            }
        } finally {
            setVerifying(false);
        }
    };

    // A user who never enrolled usually has no `mfa_enabled` field at all, so
    // "absent" counts as not enrolled — not just an explicit false.
    const showEnroll = notEnrolled || (Boolean(user) && !user.mfa_enabled);

    return (
        // A dismiss while a code is being checked would cancel the waiting
        // requests even if the code then succeeds.
        <Dialog open={open} onOpenChange={(next) => { if (!next && !verifying) cancel(); }}>
            <DialogContent className="max-w-md" data-testid="step-up-dialog">
                <DialogHeader>
                    <div className="w-11 h-11 rounded-full bg-stone-100 flex items-center justify-center mb-2">
                        <ShieldCheck className="w-5 h-5 text-stone-700" />
                    </div>
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {showEnroll ? 'Turn on two-step verification' : "Confirm it's you"}
                    </DialogTitle>
                    <DialogDescription>
                        {showEnroll
                            ? 'This needs two-step verification. Turn it on in Settings → Security, then try again.'
                            : recoveryMode
                                ? 'Enter one of your recovery codes.'
                                : 'This needs your authenticator code on this device. Enter the 6-digit code from your authenticator app.'}
                    </DialogDescription>
                </DialogHeader>

                {showEnroll ? (
                    <div className="flex justify-end gap-2" data-testid="step-up-enroll-variant">
                        <button
                            type="button"
                            onClick={cancel}
                            data-testid="step-up-cancel"
                            className="px-4 py-2 text-sm text-stone-600 hover:text-stone-900"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => { cancel(); navigate('/settings/security'); }}
                            data-testid="step-up-open-settings"
                            className="px-4 py-2 text-sm font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800"
                        >
                            Open Settings
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <MfaCodeInput
                            value={code}
                            onChange={setCode}
                            onSubmit={verify}
                            error={error}
                            disabled={verifying}
                            recoveryMode={recoveryMode}
                            onToggleRecovery={setRecoveryMode}
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={cancel}
                                disabled={verifying}
                                data-testid="step-up-cancel"
                                className="px-4 py-2 text-sm text-stone-600 hover:text-stone-900 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            {/* Recovery codes don't auto-submit; the 6-digit input
                                does, so Verify is only shown for recovery codes
                                (same rule as the sign-in code step). */}
                            {recoveryMode && (
                                <button
                                    type="button"
                                    onClick={() => verify()}
                                    disabled={verifying || !code.trim()}
                                    data-testid="step-up-verify"
                                    className="px-4 py-2 text-sm font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {verifying ? 'Verifying...' : 'Verify'}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
